import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import { authApi } from '../lib/api';
import { wsClient } from '../lib/websocket';
import { generateRandomName, generateAnonymousUserId } from '../utils/randomName';

interface AnonymousUser {
  id: string;
  username: string;
}

interface AuthState {
  user: User | null;
  anonymousUser: AnonymousUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isAnonymous: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  initializeAnonymous: () => void;
  updateAnonymousName: (name: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      anonymousUser: null,
      token: null,
      isAuthenticated: false,
      isAnonymous: false,

      login: async (email: string, password: string) => {
        const response = await authApi.login({ email, password });
        localStorage.setItem('token', response.token);
        set({ 
          user: response.user, 
          token: response.token, 
          isAuthenticated: true,
          isAnonymous: false,
          anonymousUser: null
        });
        wsClient.disconnect();
        wsClient.connect(response.token);
      },

      register: async (email: string, password: string, username: string) => {
        const response = await authApi.register({ email, password, username });
        localStorage.setItem('token', response.token);
        set({ 
          user: response.user, 
          token: response.token, 
          isAuthenticated: true,
          isAnonymous: false,
          anonymousUser: null
        });
        wsClient.disconnect();
        wsClient.connect(response.token);
      },

      logout: async () => {
        await authApi.logout();
        localStorage.removeItem('token');
        wsClient.disconnect();
        set({ user: null, token: null, isAuthenticated: false, isAnonymous: false, anonymousUser: null });
        // Reinitialize as anonymous after logout
        get().initializeAnonymous();
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const user = await authApi.getMe();
            set({ 
              user, 
              token, 
              isAuthenticated: true,
              isAnonymous: false,
              anonymousUser: null
            });
            wsClient.connect(token);
          } catch (error) {
            localStorage.removeItem('token');
            set({ user: null, token: null, isAuthenticated: false, isAnonymous: false, anonymousUser: null });
            // Initialize as anonymous if auth fails
            get().initializeAnonymous();
          }
        } else {
          // No token, initialize as anonymous
          get().initializeAnonymous();
        }
      },

      initializeAnonymous: () => {
        const state = get();
        // Only initialize if not already authenticated
        if (state.isAuthenticated || state.user) {
          return;
        }

        // Check if we have a stored anonymous user
        const stored = localStorage.getItem('anonymous-user');
        let anonymousUser: AnonymousUser;
        
        if (stored) {
          try {
            anonymousUser = JSON.parse(stored);
          } catch {
            anonymousUser = {
              id: generateAnonymousUserId(),
              username: generateRandomName(),
            };
          }
        } else {
          anonymousUser = {
            id: generateAnonymousUserId(),
            username: generateRandomName(),
          };
        }

        localStorage.setItem('anonymous-user', JSON.stringify(anonymousUser));
        set({ 
          anonymousUser, 
          isAnonymous: true, 
          isAuthenticated: true 
        });
        
        // Connect WebSocket as anonymous
        wsClient.disconnect();
        wsClient.connect(undefined, anonymousUser.id, anonymousUser.username);
      },

      updateAnonymousName: (name: string) => {
        const state = get();
        if (state.isAnonymous && state.anonymousUser && name.trim().length >= 1 && name.trim().length <= 100) {
          const updated = { ...state.anonymousUser, username: name.trim() };
          localStorage.setItem('anonymous-user', JSON.stringify(updated));
          set({ anonymousUser: updated });
          wsClient.updateAnonymousName(name.trim());
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        token: state.token,
        anonymousUser: state.anonymousUser,
      }),
    }
  )
);

